import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

function renderBoldText(text) {
  const parts = [];
  const regex = /\*\*([^*]+)\*\*/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(<strong key={`b-${match.index}`}>{match[1]}</strong>);
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

function renderSmallText(text) {
  const parts = [];
  const regex = /<small>([^<]+)<\/small>/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      const segment = text.slice(lastIndex, match.index);
      parts.push(...renderBoldText(segment));
    }
    parts.push(<SmallText key={`s-${match.index}`}>{match[1]}</SmallText>);
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    const tail = text.slice(lastIndex);
    parts.push(...renderBoldText(tail));
  }
  return parts;
}

function renderTextWithLinksAndFormatting(text) {
  const linkRegex = /\{\{([^|}]+)\|([^}]+)\}\}/g;
  const nodes = [];
  let lastIndex = 0;
  let match;
  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      const segment = text.slice(lastIndex, match.index);
      nodes.push(...renderSmallText(segment));
    }
    const label = match[1].trim();
    const href = match[2].trim();
    nodes.push(
      <a key={`a-${match.index}`} href={href} target="_blank" rel="noopener noreferrer">{label}</a>
    );
    lastIndex = linkRegex.lastIndex;
  }
  if (lastIndex < text.length) {
    const tail = text.slice(lastIndex);
    nodes.push(...renderSmallText(tail));
  }
  return nodes;
}

function selectLocalizedText(payload, language) {
  if (!payload) return '';
  const langKey = typeof language === 'string' ? language.toUpperCase() : '';
  const localized = langKey && payload[langKey] ? payload[langKey] : null;
  if (typeof localized === 'string' && localized.length > 0) return localized;
  if (typeof payload.text === 'string' && payload.text.length > 0) return payload.text;
  if (typeof payload.EN === 'string' && payload.EN.length > 0) return payload.EN;
  return '';
}

const CustomIntro = ({ id, contents = [], language, disableNext }) => {
  const checkboxItems = useMemo(() => contents.filter(c => c?.type === 'checkbox_text'), [contents]);
  const initialChecked = useMemo(() => {
    const obj = {};
    checkboxItems.forEach(c => {
      obj[c.id] = !!c?.payload?.checkedByDefault;
    });
    return obj;
  }, [checkboxItems]);

  const [checkedMap, setCheckedMap] = useState(initialChecked);

  useEffect(() => {
    // Require all checkboxes to be checked to proceed if any exist
    if (checkboxItems.length > 0) {
      const allChecked = checkboxItems.every(c => checkedMap[c.id]);
      disableNext(!allChecked);
    } else {
      disableNext(false);
    }
    return () => disableNext(false);
  }, [checkboxItems, checkedMap, disableNext]);

  const handleToggle = (checkboxId) => {
    setCheckedMap(prev => ({ ...prev, [checkboxId]: !prev[checkboxId] }));
  };

  return (
    <Container data-intro-id={id}>
      {contents.map((c) => {
        if (c.type === 'h1') {
          const text = selectLocalizedText(c?.payload, language);
          return <H1 key={c.id}>{text}</H1>;
        }
        if (c.type === 'paragraph') {
          const text = selectLocalizedText(c?.payload, language) || '';
          const lines = text.split('\n');
          return (
            <Paragraph key={c.id}>
              {lines.map((line, idx) => (
                <span key={`${c.id}-l-${idx}`}>
                  {renderTextWithLinksAndFormatting(line)}
                  {idx < lines.length - 1 && <br />}
                </span>
              ))}
            </Paragraph>
          );
        }
        if (c.type === 'image') {
          const url = c?.payload?.url || '';
          const maxHeight = c?.payload?.height || null;
          return (
            <ImageContainer key={c.id}>
              <img src={url} alt="" style={maxHeight ? { maxHeight } : undefined} loading="lazy" />
            </ImageContainer>
          );
        }
        if (c.type === 'checkbox_text') {
          const text = selectLocalizedText(c?.payload, language) || '';
          const checked = !!checkedMap[c.id];
          const lines = text.split('\n');
          return (
            <CheckboxRow key={c.id}>
              <input
                type="checkbox"
                id={`cb-${c.id}`}
                checked={checked}
                onChange={() => handleToggle(c.id)}
              />
              <label htmlFor={`cb-${c.id}`}>
                {lines.map((line, idx) => (
                  <span key={`${c.id}-cb-l-${idx}`}>
                    {renderTextWithLinksAndFormatting(line)}
                    {idx < lines.length - 1 && <br />}
                  </span>
                ))}
              </label>
            </CheckboxRow>
          );
        }
        return null;
      })}
    </Container>
  );
};

export default CustomIntro;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 600px;
`;

const H1 = styled.h1`
  font-size: 1.75rem;
  margin: 0 0 12px 0;
`;

const Paragraph = styled.p`
  font-size: 1rem;
  margin: 8px 0 12px 0;
  line-height: 1.4;
`;

const ImageContainer = styled.div`
  margin: 20px 0;
  display: flex;
  justify-content: flex-start;
  width: 100%;
  
  img {
    max-width: 100%;
    height: auto;
  }
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0;
`;

const SmallText = styled.small`
  font-size: calc(1rem - 4px);
`;

